import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { useTranslate } from '@tolgee/react';
import { useAuthService } from "../../Context/AuthContext";
import { getFlag } from "../../Helpers/FlagHandler";
import LanguageListSelector from "../Auth Components/LanguageListSelector";
import { ReactComponent as Arrow } from "../../Assets/iconsv2/generic/forwardArrow.svg";
import languageSyncManager from '../../Helpers/callHelper/LanguageSyncHelper';

const CallLanguageSettings = ({ onLanguageChange, isConference = false }) => {
    const { 
        user, 
        updateUser, 
        callTranslationsLanguages, 
        callTranslationsLanguagesRef,
        translationsLanguages,
        getTranslationsLanguages,
        handleLoginUser
    } = useAuthService();

    const [selectedLanguage, setSelectedLanguage] = useState(user?.call_language);
    const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);

    const [selectedChatLanguage, setSelectedChatLanguage] = useState(user?.language);
    const [isChatLanguageSelectorOpen, setIsChatLanguageSelectorOpen] = useState(false);

    const { t } = useTranslate();

    const verifiyIfCallLanguageIsValid = () => {
        let validLanguage = callTranslationsLanguagesRef.current.find(
            (item) => item.locale === user.call_language
        );
        return validLanguage;
    };

    const handleUpdateCallLanguage = (newLanguage) => {
        if (!newLanguage || newLanguage === user?.call_language) {
            return;
        }

        var objectData = {
            call_language: newLanguage
        };

        updateUser(
            objectData,
            function (response) {
                handleLoginUser(response.data.user);
                
                if (window.electron?.notifyLanguageChanged) {
                    window.electron.notifyLanguageChanged(newLanguage);
                }
                languageSyncManager.notifyLanguageChanged(newLanguage);
            },
            function (error) {
                
            }
        );
    };

    const handleLanguageSelection = (newLanguage) => {
        setSelectedLanguage(newLanguage);
        handleUpdateCallLanguage(newLanguage);

        if (onLanguageChange) {
            onLanguageChange(newLanguage);
        }
    };

    const displaySlectedLanguageFromList = (language) => {
        if (!callTranslationsLanguages || !language) return null;
        let found = callTranslationsLanguages.find(
            (item) => item.locale === language
        );
        return found;
    };

    const openLanguageSelector = () => {
        setIsLanguageSelectorOpen(!isLanguageSelectorOpen);
    };

    const handleUpdateChatLanguage = (newLanguage) => {
        if (!newLanguage || newLanguage === user?.language) {
            return;
        }

        var objectData = {
            language: newLanguage
        };

        updateUser(
            objectData,
            function (response) {
                handleLoginUser(response.data.user);
            },
            function (error) {

            }
        );
    };

    const handleChatLanguageSelection = (newLanguage) => {
        setSelectedChatLanguage(newLanguage);
        handleUpdateChatLanguage(newLanguage);
    };

    const displaySelectedChatLanguageFromList = (language) => {
        if (!translationsLanguages || !language) return null;
        let found = translationsLanguages.find(
            (item) => item.name === language
        );
        return found;
    };

    const openChatLanguageSelector = () => {
        setIsChatLanguageSelectorOpen(!isChatLanguageSelectorOpen);
    };

    const handleSetCallLanguage = () => {
        if (!user) {
            return;
        }
        let foundLanguage = verifiyIfCallLanguageIsValid();
        if (foundLanguage) {
            setSelectedLanguage(foundLanguage.locale);
        } else {
            setSelectedLanguage(null);
        }
    };

    useEffect(() => {
        if (window.electron?.onLanguageChanged) {
            const unsubscribe = window.electron.onLanguageChanged((language) => {
                setSelectedLanguage(language);
            });
            return typeof unsubscribe === "function" ? unsubscribe : undefined;
        }
    }, []);

    useEffect(() => {
        const cleanup = languageSyncManager.onLanguageChanged((language) => {
            setSelectedLanguage(language);
        });

        return cleanup;
    }, []);

    useEffect(() => {
        if (user?.call_language !== undefined && user.call_language !== selectedLanguage) {
            setSelectedLanguage(user.call_language);
        }
    }, [user?.call_language]);

    useEffect(() => {
        if (user?.language !== undefined && user.language !== selectedChatLanguage) {
            setSelectedChatLanguage(user.language);
        }
    }, [user?.language]);

    useEffect(() => {
        handleSetCallLanguage();
    }, [user, callTranslationsLanguages]);

    useEffect(() => {
        if (isConference && (!translationsLanguages || translationsLanguages.length === 0)) {
            getTranslationsLanguages(() => {}, () => {});
        }
    }, [isConference]);

    return (
        <Container>
            {isLanguageSelectorOpen && (
                <LanguageListSelector
                    isLanguageSelectorOpen={isLanguageSelectorOpen}
                    openLanguageList={openLanguageSelector}
                    valuesList={callTranslationsLanguages}
                    setValue={handleLanguageSelection}
                    selectedValue={selectedLanguage}
                    callLanguage
                />
            )}
            {isChatLanguageSelectorOpen && (
                <LanguageListSelector
                    isLanguageSelectorOpen={isChatLanguageSelectorOpen}
                    openLanguageList={openChatLanguageSelector}
                    valuesList={translationsLanguages}
                    setValue={handleChatLanguageSelection}
                    selectedValue={selectedChatLanguage}
                />
            )}
            <Group>
                <GroupSpan>
                    {isConference ? t('call_modal.conference_language') : t('profile.language')}
                </GroupSpan>
                {displaySlectedLanguageFromList(selectedLanguage) ? (
                    <LanguageItem onClick={() => openLanguageSelector()}>
                        <Flag
                            src={getFlag(
                                displaySlectedLanguageFromList(selectedLanguage)?.country_code
                            )}
                            alt={selectedLanguage}
                        />
                        {displaySlectedLanguageFromList(selectedLanguage)?.name} (
                        {displaySlectedLanguageFromList(selectedLanguage)?.translation})
                        <Arrow style={{ marginLeft: "auto" }} />
                    </LanguageItem>
                ) : (
                    <CustomInput
                        readOnly
                        value={t('settings.select_language') || "Select language"}
                        onClick={() => openLanguageSelector()}
                    />
                )}
            </Group>
            {isConference && (
                <Group>
                    <GroupSpan>
                        {t('register.chat_language')}
                    </GroupSpan>
                    {displaySelectedChatLanguageFromList(selectedChatLanguage) ? (
                        <LanguageItem onClick={() => openChatLanguageSelector()}>
                            <Flag
                                src={getFlag(
                                    displaySelectedChatLanguageFromList(selectedChatLanguage)?.country_code
                                )}
                                alt={selectedChatLanguage}
                            />
                            {displaySelectedChatLanguageFromList(selectedChatLanguage)?.name} (
                            {displaySelectedChatLanguageFromList(selectedChatLanguage)?.translation})
                            <Arrow style={{ marginLeft: "auto" }} />
                        </LanguageItem>
                    ) : (
                        <CustomInput
                            readOnly
                            value={t('settings.select_language')}
                            onClick={() => openChatLanguageSelector()}
                        />
                    )}
                </Group>
            )}
        </Container>
    )
}

export default CallLanguageSettings

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;
`

const Group = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const GroupSpan = styled.p`
    font-size: 16px;
    line-height: 18px;
    font-weight: 500;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
`

const LanguageItem = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    background: ${({ theme }) => theme.menu.groupContentBackground};
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
    cursor: pointer;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.menu.groupContentBackground};
    
    &:hover {
        background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
    }
`

const Flag = styled.img`
    width: 20px;
    height: 20px;
    border-radius: 100px;
    object-fit: cover;
`

const CustomInput = styled.input`
    width: 100%;
    background: ${({ theme }) => theme.menu.groupContentBackground};
    border: 1px solid ${({ theme }) => theme.menu.groupContentBackground};
    border-radius: 4px;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    padding: 10px 12px;
    cursor: pointer;
    
    &:focus-visible {
        outline: none;
    }
    
    &:hover {
        background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
    }
`